<template>
    <div class="center">
        <NetworkStatus/>
        <Navbar v-bind:hideMenu="true" />

        <div class="page-content">
            <div class="spacer-40"></div>
            <h1 class="title">{{$t('get_started')}}</h1>
            <p class="center-text">{{$t('interested_in_partnering_with')}}</p>
            <p class="center-text">{{$t('you_set_your_own_cost')}}</p>
            <div class="spacer-40"></div>

            <form id="signupForm">
                <b-field grouped>
                    <b-field :label="$t('first_name') + ' *'" style="width:49%">
                        <b-input v-model="form.first_name" type="text" autocomplete="given-name" required></b-input>
                    </b-field>

                    <b-field :label="$t('last_name') + ' *'" style="width:49%">
                        <b-input v-model="form.last_name" type="text" autocomplete="family-name" required></b-input>
                    </b-field>
                </b-field>

                <b-field :label="$t('email_address') + ' *'">
                    <b-input v-model="form.email_address" type="email" autocomplete="email" required></b-input>
                </b-field>

                <b-field>
                    <template v-slot:label>
                        {{$t('additional_email_addresses')}}
                        <b-tooltip type="is-dark" :label="$t('additional_email_addresses_tooltip')" multilined>
                            <b-icon size="is-small" icon="help-circle-outline"></b-icon>
                        </b-tooltip>
                    </template>

                    <div>
                        <div class="inline">
                            <form autocomplete="off">
                                <b-input v-model="form.additional_email_address" type="email" :placeholder="$t('enter_additional_email_address')"></b-input>
                            </form>
                            <b-button class="is-small left-spacer-20" @click="addEmailAddress">{{$t('add_email_address')}}</b-button>
                        </div>
                        <div v-if="form.additional_email_addresses && form.additional_email_addresses.length > 0" class="top-spacer-20">
                            <ul class="email-list">
                                <li v-for="(email, index) in form.additional_email_addresses" :key="index">
                                    {{ email }} <span class="delete is-small" @click="deleteEmailAddress(index)"></span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </b-field>

                <b-field :label="$t('company') + ' *'">
                    <b-input v-model="form.company" type="text" autocomplete="organization" required></b-input>
                </b-field>

                <b-field :label="$t('country') + ' *'">
                    <b-select v-model="form.country_code" required>
                        <option v-for="country in countries"
                                :key="country.country_code"
                                :value="country.country_code"
                                :selected="country.country_code === form.country_code">
                            {{country.country_name }}
                        </option>
                    </b-select>
                </b-field>

                <b-field :type="{'is-danger': passwordInvalid}" :message="{'Passwords need to be at least 8 characters long, contain an upper and lowercase character, and have at least one number or special character': passwordInvalid }">
                    <template v-slot:label>
                        {{$t('password')}} *
                        <b-tooltip type="is-dark" :label="$t('password_rules')" multilined>
                            <b-icon size="is-small" icon="help-circle-outline"></b-icon>
                        </b-tooltip>
                    </template>
                    <b-input v-model="form.password" type="password" v-on:blur="validatePasswordRules" password-reveal required></b-input>
                </b-field>

                <b-field :label="$t('repeat-password') + ' *'">
                    <b-input v-model="form.password2" type="password" password-reveal required></b-input>
                </b-field>

                <b-field>
                    <b-checkbox v-model="form.agree" :value="true"></b-checkbox>
                    <span> {{$t('i_agree_to_the')}} <a href="terms.html" target="_blank">{{$t('terms')}}</a> {{$t('and')}} <a href="https://www.sift.co/privacy" target="_blank">{{$t('privacy_policy')}}</a></span>
                </b-field>
            </form>

            <ErrorMessages :errors="errors"></ErrorMessages>

            <div class="button-container">
                <vue-recaptcha v-if="!isLocalhost"
                               ref="recaptcha"
                               @verify="onVerify"
                               @expired="onExpired"
                               sitekey="6LchzkwbAAAAAMDLz8M-5cuvLel-sXp94YjvORNX"
                               :loadRecaptchaScript="true">
                    <button class="ds-button button-small w-button">
                        {{$t('create_account')}}
                    </button>
                </vue-recaptcha>
                <button v-else class="ds-button button-small w-button" v-on:click="createAccountLocal">
                    {{$t('create_account')}}
                </button>
            </div>

            {{$t('have_an_account')}} <a href="login.html">{{$t('log_in')}}</a>
        </div>

        <Footer/>
        <CookieBar/>
    </div>
</template>

<style>
    .page-content {
        padding-top: 20px;
    }
    p.center-text {
        text-align: center;
        font-size: 18px;
        padding: 5px;
    }
    h1.title {
        padding-bottom: 10px;
    }
    .field:not(:last-child) {
        margin-bottom: 1.25rem;
    }
    .field.is-grouped {
        margin-bottom: 0;
    }
    div.spacer-40 {
        margin-top: 40px;
    }
    .top-spacer-20 {
        margin-top: 20px;
    }
    .left-spacer-20 {
        margin-left: 20px;
    }
    .inline {
        display: inline-flex;
    }
    ul.email-list {
        list-style: square inside;
        margin-left: 2em;
    }
    ul.email-list li {
        margin-bottom: 0.5em;
    }
    span.delete {
        margin-top: 4px;
        margin-left: 5px;
    }
</style>

<script>
    import NetworkStatus from "../components/NetworkStatus";
    import Navbar from "../components/Navbar";
    import Footer from "../components/Footer";
    import CookieBar from "../components/CookieBar";
    import ErrorMessages from "../components/ErrorMessages";
    import VueRecaptcha from 'vue-recaptcha';
    import session from '../session'

    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

    function successHandler() {
        session.navigateToPage('thankyou.html');
    }

    function errorHandler() {
    }

    const model = {
        countries: [],
        additional_email_address: null,
        form: {
            first_name: null,
            last_name: null,
            email_address: null,
            additional_email_addresses: [],
            company: null,
            country_code: null,
            password: null,
            password2: null,
            agree: false,
            recaptcha_token: null,
        },
        is_robot: true,
        errors: [],
        passwordInvalid: false,
        isLocalhost: isLocalhost
    };

    function getCountriesSuccessHandler(responseData) {
        model.form.country_code = responseData.ip_country_code;
        model.countries = responseData.countries
    }

    function getCountriesErrorHandler() {
        model.form.country_code = "US";
        model.countries = [
            {
                country_code: "US",
                country_name: "United States"
            }
        ];
    }

    session.get('countries', getCountriesSuccessHandler, getCountriesErrorHandler, model.errors);

    function validate() {
        model.errors.splice(0, model.errors.length);
        const passwordOK = model.form.password === model.form.password2;
        if (!passwordOK) {
            model.errors.push('PASSWORDS_DO_NOT_MATCH');
        }
        if (!model.form.agree) {
            model.errors.push('MUST_AGREE');
        }
        const valid = document.getElementById('signupForm').checkValidity();
        if (!valid) {
            model.errors.push('ALL_FIELDS_ARE_REQUIRED');
        }
        return model.errors.length === 0 && model.passwordInvalid === false;
    }

    export default {
        components: {
            NetworkStatus, Navbar, Footer, CookieBar, ErrorMessages, VueRecaptcha
        },

        data() {
            return model;
        },

        methods: {
            addEmailAddress: function() {
                if (model.form.additional_email_address) {
                    var found = false;
                    for (let i = 0; i < model.form.additional_email_addresses.length; i++) {
                        if (model.form.additional_email_addresses[i] === model.form.additional_email_address) {
                            found = true;
                            break;
                        }
                    }
                    if (model.form.additional_email_address === model.form.email_address) {
                        found = true;
                    }
                    if (!found) {
                        model.form.additional_email_addresses.push(model.form.additional_email_address);
                        model.form.additional_email_address = null;
                    }
                }
            },
            deleteEmailAddress: function(index) {
                if (index < model.form.additional_email_addresses.length) {
                    model.form.additional_email_addresses.splice(index, 1);
                }
            },
            validatePasswordRules: function () {
                const lower = /.*[a-z]+.*$/;
                const upper = /.*[A-Z]+.*$/;
                const other = /.*[^a-zA-Z]+.*$/;
                model.passwordInvalid = !(model.form.password.length >= 8 && model.form.password.match(lower) && model.form.password.match(upper) && model.form.password.match(other));
            },
            createAccountLocal: function () {
                if (validate()) {
                    model.form.recaptcha_token = 'local';
                    session.signup(model.form, successHandler, errorHandler, model.errors)
                }
            },
            onVerify: function (response) {
                if (response && validate()) {
                    model.form.recaptcha_token = response;
                    session.signup(model.form, successHandler, errorHandler, model.errors)
                }
            },
            onExpired: function () {
                model.form.recaptcha_token = null;
                this.$refs.recaptcha.reset();
            }
        }
    }
</script>